/**
 * Web product catalogue. Offer composition is independent of release status,
 * user permissions and the existing rollout switches.
 *
 * This module is data only: no Firebase, storage, React or menu dependencies.
 * Stable feature IDs are the seam for the future local access context.
 * AKONGO operating switches (automatic comment translation) are not sold to
 * parks: they stay out of this catalogue, see src/product-access/akongoInternalFlags.js.
 */
export const PRODUCT_CATALOG_VERSION = 1;

export const PRODUCT_FEATURE_CATEGORIES = Object.freeze([
  "workspace", "assessments", "observations", "monitoring", "logbook",
  "integrations", "extensions",
]);

const feature = (id, category, messageKey, options = {}) => Object.freeze({
  id,
  category,
  messageKey,
  status: "stable",
  ...options,
});

export const PRODUCT_FEATURES = Object.freeze([
  feature("workspace.animals", "workspace", "animals"),
  feature("workspace.groups", "workspace", "groups"),
  feature("workspace.enclosures", "workspace", "enclosures"),
  feature("workspace.sectors", "workspace", "sectors"),
  feature("workspace.users", "workspace", "users"),
  feature("workspace.support", "workspace", "support"),
  feature("exports.excel", "workspace", "excel"),
  feature("assessments.rapid", "assessments", "rapidAssessment"),
  feature("assessments.history", "assessments", "assessmentHistory"),
  feature("assessments.reports", "assessments", "assessmentReports"),
  feature("welfare.goals", "assessments", "welfareGoals"),
  feature("assessments.individual", "assessments", "individualAssessment", {
    legacyFlag: "individualFastEval", releasedAt: "2026-06",
  }),
  feature("assessments.species", "assessments", "speciesAssessment"),
  feature("assessments.customGrids", "assessments", "customGridUse"),
  feature("assessments.gridAuthoring", "assessments", "gridAuthoring", {
    legacyFlag: "customGrids", releasedAt: "2026-08",
  }),
  feature("observations.campaigns", "observations", "campaigns"),
  feature("observations.planning", "observations", "observationPlanning"),
  feature("observations.focal", "observations", "focal"),
  feature("observations.scan", "observations", "scan"),
  feature("observations.ethograms", "observations", "ethograms"),
  feature("observations.focalAnalysis", "observations", "focalAnalysis"),
  feature("observations.scanAnalysis", "observations", "scanAnalysis"),
  feature("observations.training", "observations", "observerTraining", {
    status: "beta", legacyFlag: "obsTraining", releasedAt: "2026-08",
  }),
  feature("monitoring.enrichment", "monitoring", "enrichmentMonitoring"),
  feature("monitoring.enrichmentAnalysis", "monitoring", "enrichmentAnalysis"),
  feature("monitoring.training", "monitoring", "training", {
    legacyFlag: "training", releasedAt: "2026-07",
  }),
  feature("monitoring.geriatric", "monitoring", "geriatric"),
  feature("monitoring.individualParameters", "monitoring", "individualParameters"),
  feature("monitoring.environmentalParameters", "monitoring", "environmentalParameters"),
  feature("monitoring.crossAnalysis", "monitoring", "crossAnalysis"),
  feature("logbook.entries", "logbook", "logbookEntries"),
  feature("logbook.dailyReports", "logbook", "dailyReports"),
  feature("logbook.templates", "logbook", "reportTemplates"),
  feature("logbook.tasks", "logbook", "tasks"),
  feature("logbook.recurringTasks", "logbook", "recurringTasks"),
  feature("logbook.anomalyAlerts", "logbook", "anomalyAlerts"),
  feature("logbook.rounds", "logbook", "rounds"),
  feature("logbook.treatments", "logbook", "treatments"),
  feature("logbook.pharmacy", "logbook", "pharmacy"),
  feature("logbook.assistant", "logbook", "logbookAssistant"),
  feature("zims.import", "integrations", "zimsImport"),
  feature("zims.syncIn", "integrations", "zimsSyncIn"),
  feature("zims.syncOut", "integrations", "zimsSyncOut"),
  feature("integrations.api", "integrations", "api"),
  feature("extensions.enrichmentGuides", "extensions", "enrichmentGuides", {
    status: "beta", legacyFlag: "enrichmentGuides", releasedAt: "2026-07",
  }),
  feature("extensions.technical", "extensions", "technical", {
    status: "beta", legacyFlag: "technicalModule", releasedAt: "2026-08",
  }),
  feature("extensions.weather", "extensions", "weather"),
  feature("extensions.engagement", "extensions", "engagement"),
  feature("extensions.rafiki", "extensions", "rafiki"),
  feature("extensions.advancedReports", "extensions", "advancedReports"),
  // Projets scientifiques du parc (V2 seule) : bêta incluse dans Progress (24/10/2026) ;
  // ailleurs, ajout à la carte.
  feature("extensions.researchProjects", "extensions", "researchProjects", {
    status: "beta", legacyFlag: "researchProjects", releasedAt: "2026-10",
  }),
  feature("extensions.multiZoo", "extensions", "multiZoo", { status: "beta" }),
]);

export const PRODUCT_FEATURE_BY_ID = Object.freeze(Object.fromEntries(
  PRODUCT_FEATURES.map((item) => [item.id, item])
));

// This is an explicit composition, not a numeric tier hierarchy. In particular,
// the logbook is usable alone and does not grant Progress analysis tools.
const CORE = Object.freeze(["workspace.users", "workspace.support", "exports.excel"]);
// Collection workspace (individuals, enclosures, sectors): every offer above
// Discovery, and the logbook, which is organised by sector. Discovery mirrors the
// historical tier: rapid assessments, welfare goals, and group management (2026-09-13).
const COLLECTION = Object.freeze([
  "workspace.animals", "workspace.enclosures", "workspace.sectors",
]);
const DISCOVERY = Object.freeze(["workspace.groups", "assessments.rapid", "welfare.goals"]);
const ESSENTIAL = Object.freeze([
  ...COLLECTION, ...DISCOVERY, "assessments.history", "assessments.reports",
  "assessments.individual", "assessments.species", "assessments.customGrids", "assessments.gridAuthoring",
  "observations.campaigns", "observations.focal", "observations.scan",
  "observations.ethograms", "observations.focalAnalysis", "observations.scanAnalysis",
  "zims.import", "zims.syncIn", "extensions.weather",
]);
const PROGRESS = Object.freeze([
  ...ESSENTIAL, "observations.planning", "monitoring.enrichment", "monitoring.enrichmentAnalysis",
  "monitoring.training", "monitoring.geriatric", "monitoring.individualParameters",
  "monitoring.environmentalParameters", "monitoring.crossAnalysis", "zims.syncOut",
  "extensions.researchProjects",
]);
const LOGBOOK = Object.freeze([
  ...COLLECTION, "workspace.groups",
  "logbook.entries", "logbook.dailyReports", "logbook.templates", "logbook.tasks",
  "logbook.recurringTasks", "logbook.anomalyAlerts", "logbook.rounds", "logbook.treatments",
  "logbook.pharmacy", "logbook.assistant", "extensions.weather",
]);

export const PRODUCT_OFFER_PRESETS = Object.freeze({
  discovery: Object.freeze({ id: "discovery", features: DISCOVERY }),
  essential: Object.freeze({ id: "essential", features: ESSENTIAL }),
  progress: Object.freeze({ id: "progress", features: PROGRESS }),
});

export const PRODUCT_PROGRAM_PRESETS = Object.freeze({
  logbook: Object.freeze({ id: "logbook", features: LOGBOOK }),
});

/** Existing rollout keys keep their storage and backend meaning unchanged. */
export const LEGACY_PRODUCT_FLAGS = Object.freeze(PRODUCT_FEATURES.filter(
  (item) => item.legacyFlag
));

/**
 * Compose the functional offer locally. Included features are mandatory: an
 * explicit false only removes an individual addition, never a preset grant.
 * Unknown IDs/tiers grant nothing; legacy FullCare needs an explicit mapping.
 * When the park document carries the list of enabled ids written at save time,
 * that list is the real grant: a client can read it without knowing the preset.
 * The result does not claim to authorize a user or to enforce server access.
 */
export function resolveProductFeatures({ tier = null, programs = [], additions = {}, features: listed = null } = {}) {
  const offer = Object.prototype.hasOwnProperty.call(PRODUCT_OFFER_PRESETS, tier)
    ? PRODUCT_OFFER_PRESETS[tier] : null;
  const programIds = Array.isArray(programs) ? [...new Set(programs)].filter(
    (id) => Object.prototype.hasOwnProperty.call(PRODUCT_PROGRAM_PRESETS, id)
  ) : [];
  const sources = Object.fromEntries(PRODUCT_FEATURES.map(({ id }) => [id, []]));
  const addPreset = (ids, source) => ids.forEach((id) => sources[id].push(source));
  if (offer || programIds.length) addPreset(CORE, "core");
  if (offer) addPreset(offer.features, offer.id);
  programIds.forEach((id) => addPreset(PRODUCT_PROGRAM_PRESETS[id].features, id));
  const explicit = additions && typeof additions === "object" && !Array.isArray(additions)
    ? additions : {};
  const grants = Array.isArray(listed) ? new Set(listed) : null;
  const features = Object.freeze(Object.fromEntries(PRODUCT_FEATURES.map(({ id }) => {
    const included = sources[id].length > 0;
    const additional = Object.prototype.hasOwnProperty.call(explicit, id) && explicit[id] === true;
    return [id, Object.freeze({
      id, included, additional, enabled: grants ? grants.has(id) : included || additional,
      sources: Object.freeze([...sources[id]]),
    })];
  })));
  return Object.freeze({
    catalogVersion: PRODUCT_CATALOG_VERSION,
    tier: offer?.id || null,
    unknownTier: tier != null && tier !== "" && !offer,
    listed: grants !== null,
    programs: Object.freeze(programIds),
    features,
    can: (id) => Object.prototype.hasOwnProperty.call(features, id) && features[id].enabled,
  });
}

/** Catalogue order list of the ids a resolved offer enables; stored with the park at save time. */
export const enabledFeatureIds = (resolved) => PRODUCT_FEATURES.map(({ id }) => id)
  .filter((id) => resolved.features[id].enabled);
